import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLockConfig, checkLocked } from '../hooks/useLockConfig';
import {
  Typography, Form, Select, DatePicker, Input, Button, Tag, Space,
  Card, Spin, message, Divider, List, Avatar, Modal, Alert, Popconfirm, Descriptions, Switch, Checkbox,
} from 'antd';
import { UserOutlined, LinkOutlined, SaveOutlined, PaperClipOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import CommentBox, { CommentAttachments } from '../components/CommentBox';
import LinkifiedText from '../components/LinkifiedText';
import { useBackFallback, usePageLabel } from '../components/BackChip';

// Inactive still exists server-side (and older records may carry it) but is
// no longer offered anywhere in the UI.
const EDIT_STATUS_OPTIONS = [
  { label: 'Draft',       value: 'draft' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Complete',    value: 'complete' },
  { label: 'Canceled',  value: 'canceled' },
];
const isFinalStatus = (s) => s === 'complete' || s === 'inactive';

const WARNING_OPTIONS = [
  { label: '1st Warning',   value: '1st' },
  { label: '2nd Warning',   value: '2nd' },
  { label: '3rd Warning',   value: '3rd' },
  { label: 'Final Warning', value: 'Final' },
];

const STATUS_COLOR = { draft: 'default', in_progress: 'blue', complete: 'green', inactive: 'gold', canceled: 'red' };
const WARNING_COLOR = { '1st': 'blue', '2nd': 'orange', '3rd': 'volcano', 'Final': 'red' };

const SOURCE_LABEL = { ai_notification: 'AI Notification', error_submission: 'Error Log' };
const SOURCE_PATH  = { ai_notification: '/notifications', error_submission: '/errors' };

export default function ComplianceDetailPage({ type }) {
  const { id }      = useParams();
  const isNew       = id === 'new';
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);
  // The Back chip (Layout): back where you came from, else this list.
  const writeup = type === 'writeup';
  const staffComp = (currentUser?.[writeup ? 'CanEditFormalWriteups' : 'CanEditIncidents'] ?? 0) >= 1;
  useBackFallback(
    staffComp ? (writeup ? '/compliance/writeups' : '/compliance/incidents') : '/compliance/my-issues',
    staffComp ? (writeup ? 'Formal Writeups' : 'Compliance Incidents') : 'My Compliance Issues');
  usePageLabel(`${writeup ? 'Formal Writeup' : 'Compliance Incident'} #${id}`);

  const isWriteup   = type === 'writeup';
  const isAgent     = currentUser?.Role === 'agent';
  const isAdmin     = currentUser?.Role === 'admin';
  const isQaAdmin   = currentUser?.Role === 'qa_admin';
  const basePath = isWriteup ? '/compliance/writeups' : '/compliance/incidents';
  const title    = isWriteup ? 'Formal Writeup' : 'Compliance Incident';
  // Stored as AgreedActionPlan; shown under this label on both types.
  const actionPlanLabel = 'Consequences should incident occur again';

  const [form]         = Form.useForm();

  // Expiry dates default to 30 days after the incident date (or today, when
  // there is none yet) the moment a warning is picked or QA Related turned on.
  // Only an empty date is filled, so a date someone chose is never overwritten.
  const defaultExpiries = (changed) => {
    const base = form.getFieldValue('date_of_incident');
    const def = (base ? dayjs(base) : dayjs()).add(30, 'day');
    if (changed.warning_number && !form.getFieldValue('warning_expires_on')) {
      form.setFieldValue('warning_expires_on', def);
    }
    if (changed.is_qa_related && !form.getFieldValue('qa_related_expires_on')) {
      form.setFieldValue('qa_related_expires_on', def);
    }
  };
  const [incident, setIncident]   = useState(null);
  const [loading, setLoading]     = useState(!isNew);
  const [saving, setSaving]       = useState(false);
  const [allUsers, setAllUsers]       = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);
  const [allIssues, setAllIssues]   = useState([]);
  const [allTeams, setAllTeams]     = useState([]);
  const [employeeHasTeam, setEmployeeHasTeam] = useState(false);
  const [formTeamId, setFormTeamId] = useState(null);
  const [commenting, setCommenting]     = useState(false);
  const [newComment, setNewComment]     = useState('');
  const [acknowledging, setAcknowledging] = useState(false);
  const [canceling,  setCanceling]         = useState(false);
  const [resetting,  setResetting]         = useState(false);
  const commentRef = useRef(null);
  const [printOptOpen,  setPrintOptOpen]  = useState(false);
  const [printComments, setPrintComments] = useState(false);
  const printTitleRef   = useRef(null);
  const pendingPrintRef = useRef(false);
  useEffect(() => {
    const restore = () => {
      if (printTitleRef.current != null) {
        document.title = printTitleRef.current;
        printTitleRef.current = null;
      }
    };
    window.addEventListener('afterprint', restore);
    return () => {
      window.removeEventListener('afterprint', restore);
      restore();
    };
  }, []);

  const isAssignedTo = !isNew && incident?.AssignedToUserID === currentUser?.ID;
  const hasEditPerm = isWriteup ? (currentUser?.CanEditFormalWriteups ?? 0) >= 2 : (currentUser?.CanEditIncidents ?? 0) >= 2;
  const isManager   = isWriteup ? (currentUser?.CanEditFormalWriteups ?? 0) >= 3 : (currentUser?.CanEditIncidents ?? 0) >= 3;
  const canEdit     = (isNew || isManager || isAssignedTo) && hasEditPerm;

  // Prefill from URL params (when created from another page e.g. AINotification)
  const searchParams    = new URLSearchParams(window.location.search);
  const prefillUser     = searchParams.get('employee_user_id');
  const prefillSource   = searchParams.get('source_type');
  const prefillSrcId    = searchParams.get('source_id');
  const prefillDate     = searchParams.get('incident_date');

  useEffect(() => {
    api.get('/api/users').then((r) => setAllUsers(r.data)).catch(() => {});
    api.get('/api/compliance/issues?active_only=1').then((r) => setAllIssues(r.data)).catch(() => {});
    api.get('/api/teams?my_teams=1').then((r) => setAllTeams(r.data)).catch(() => {});
  }, []);

  // Reload assignable users whenever the team or the loaded incident changes
  useEffect(() => {
    const editParam = isWriteup ? { can_edit_formal_writeups: 1 } : { can_edit_incidents: 1 };
    const params = { ...editParam };
    if (formTeamId) params.team_id = formTeamId;
    if (!isNew && incident?.AssignedToUserID) params.include_user_id = incident.AssignedToUserID;
    api.get('/api/users', { params }).then((r) => setAssignUsers(r.data)).catch(() => {});
  }, [formTeamId]); // eslint-disable-line

  // Default Assigned To → current user on new records once the user list loads
  useEffect(() => {
    if (!isNew || !allUsers.length || !currentUser?.ID) return;
    const eligible = allUsers.some(
      (u) => u.IsActive && (isWriteup ? (u.CanEditFormalWriteups ?? 0) >= 2 : (u.CanEditIncidents ?? 0) >= 2) && u.ID === currentUser.ID
    );
    if (eligible) form.setFieldValue('assigned_to_user_id', currentUser.ID);
  }, [allUsers]); // eslint-disable-line

  // Pre-populate team from QAUsers.TeamID when employee is pre-filled via URL param
  useEffect(() => {
    if (!isNew || !prefillUser || !allUsers.length) return;
    const user = allUsers.find((u) => u.ID === parseInt(prefillUser, 10));
    if (user?.TeamID) {
      form.setFieldValue('team_id', user.TeamID);
      setEmployeeHasTeam(true);
      setFormTeamId(user.TeamID);
    }
  }, [allUsers]); // eslint-disable-line

  // In edit mode, lock the team field and set formTeamId for assignee filtering
  useEffect(() => {
    if (isNew || !incident) return;
    setEmployeeHasTeam(!!incident.TeamID);
    setFormTeamId(incident.TeamID || null);
  }, [incident, isNew]); // eslint-disable-line

  useEffect(() => {
    if (isNew) {
      if (prefillUser) form.setFieldValue('employee_user_id', parseInt(prefillUser, 10));
      if (prefillDate) form.setFieldValue('date_of_incident', dayjs(prefillDate));
      return;
    }
    setLoading(true);
    api.get(`/api/compliance/${id}`)
      .then((r) => {
        const inc = r.data;
        setIncident(inc);
        form.setFieldsValue({
          employee_user_id:    inc.EmployeeUserID,
          assigned_to_user_id: inc.AssignedToUserID ?? undefined,
          team_id:             inc.TeamID ?? undefined,
          date_of_incident:    inc.DateOfIncident ? dayjs(inc.DateOfIncident) : null,
          status:              inc.Status,
          description:         inc.Description ?? '',
          agreed_action_plan:  inc.AgreedActionPlan ?? '',
          warning_number:      inc.WarningNumber ?? undefined,
          warning_expires_on:  inc.WarningExpiresOn ? dayjs(inc.WarningExpiresOn) : null,
          is_qa_related:       !!inc.IsQARelated,
          qa_related_expires_on: inc.QARelatedExpiresOn ? dayjs(inc.QARelatedExpiresOn) : null,
          issue_ids:           (inc.Issues || []).map((iss) => iss.IssueID),
        });
      })
      .catch(() => message.error('Failed to load record.'))
      .finally(() => setLoading(false));
  }, [id, isNew]); // eslint-disable-line

  const handleStatusChange = (newStatus) => {
    if (newStatus === 'canceled') {
      Modal.confirm({
        title: `Cancel this ${title}?`,
        content: 'Once canceled, the record will be locked and cannot be edited.',
        okText: 'Yes, cancel it',
        cancelText: 'Go back',
        onOk:     () => handleSave(),
        onCancel: () => form.setFieldValue('status', incident?.Status ?? 'draft'),
      });
    }
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    if (isNew && values.date_of_incident) {
      const dateStr = values.date_of_incident.format('YYYY-MM-DD');
      if (checkLocked(lockConfig, isWriteup ? 'writeup' : 'compliance', dateStr)) {
        message.error('This date is before the configured lock date. Records cannot be created before the lock date.');
        return;
      }
    }
    setSaving(true);
    try {
      const body = {
        type,
        employee_user_id:    values.employee_user_id,
        assigned_to_user_id: values.assigned_to_user_id ?? null,
        date_of_incident:    values.date_of_incident?.format('YYYY-MM-DD') ?? null,
        status:              values.status,
        description:         values.description ?? null,
        agreed_action_plan:  values.agreed_action_plan ?? null,
        warning_number:      values.warning_number ?? null,
        warning_expires_on:  values.warning_number ? (values.warning_expires_on?.format('YYYY-MM-DD') ?? null) : null,
        is_qa_related:       !!values.is_qa_related,
        qa_related_expires_on: values.is_qa_related ? (values.qa_related_expires_on?.format('YYYY-MM-DD') ?? null) : null,
        issue_ids:           values.issue_ids ?? [],
        team_id: values.team_id ?? null,
      };

      if (isNew) {
        if (prefillSource && prefillSrcId) {
          body.source_type = prefillSource;
          body.source_id   = parseInt(prefillSrcId, 10);
        }
        const res = await api.post('/api/compliance', body);
        message.success(`${title} created.`);
        navigate(`${basePath}/${res.data.id}`, { replace: true });
      } else {
        await api.put(`/api/compliance/${id}`, body);
        if (newComment.trim()) {
          await api.post(`/api/compliance/${id}/comments`, { comment: newComment.trim() });
          setNewComment('');
        }
        if (values.status === 'complete') {
          message.warning('Record marked as Complete. Use Reset to Draft if corrections are needed.', 6);
        } else {
          message.success('Saved.');
        }
        const res = await api.get(`/api/compliance/${id}`);
        setIncident(res.data);
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await api.post(`/api/compliance/${id}/acknowledge`);
      message.success('Acknowledged.');
      const res = await api.get(`/api/compliance/${id}`);
      setIncident(res.data);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to acknowledge.');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleResetToDraft = async () => {
    setResetting(true);
    try {
      await api.put(`/api/compliance/${id}`, { status: 'draft' });
      message.success('Status reset to Draft.');
      const res = await api.get(`/api/compliance/${id}`);
      setIncident(res.data);
      form.setFieldValue('status', 'draft');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to reset to draft.');
    } finally {
      setResetting(false);
    }
  };


  const handleSetCanceled = async () => {
    setCanceling(true);
    try {
      await api.put(`/api/compliance/${id}`, { status: 'canceled' });
      message.success('Status set to Canceled.');
      const res = await api.get(`/api/compliance/${id}`);
      setIncident(res.data);
      form.setFieldValue('status', 'canceled');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to cancel.');
    } finally {
      setCanceling(false);
    }
  };

  const handleCancelAfterAck = async () => {
    setSaving(true);
    try {
      await api.put(`/api/compliance/${id}`, { status: 'canceled' });
      message.success('Record canceled.');
      const res = await api.get(`/api/compliance/${id}`);
      setIncident(res.data);
      form.setFieldValue('status', 'canceled');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to cancel.');
    } finally {
      setSaving(false);
    }
  };

  const handleComment = async (text, files) => {
    setCommenting(true);
    try {
      const res = await api.post(`/api/compliance/${id}/comments`, { comment: text });
      const commentId = res.data.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', 'compliance');
        fd.append('comment_id', String(commentId));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
      const updated = await api.get(`/api/compliance/${id}`);
      setIncident(updated.data);
      setTimeout(() => commentRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      message.error('Failed to add comment.');
    } finally {
      setCommenting(false);
    }
  };

  const lockConfig = useLockConfig();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>;
  }

  const isLocked       = !isNew && ['complete', 'inactive', 'canceled'].includes(incident?.Status);
  const isAcknowledged = !isNew && !!incident?.AcknowledgedAt;
  const isSelfEmployee = !isNew && incident?.EmployeeUserID === currentUser?.ID;
  const canEditFields  = canEdit && !isLocked && !isSelfEmployee;
  // Print: never for the employee on the record (or any agent).
  const canPrint       = !isNew && !!incident && !isAgent && !isSelfEmployee;

  // Same approach as the evaluation page: print runs from the options modal's
  // afterClose, so Ant's mask and scroll lock are gone before window.print()
  // blocks the page; the tab title becomes the PDF's default file name.
  const doPrint = () => {
    const slug  = (str) => (str || '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    const date  = incident?.DateOfIncident?.slice(0, 10) || dayjs().format('YYYY-MM-DD');
    printTitleRef.current = document.title;
    document.title = `${date}_${id}_${isWriteup ? 'Writeup' : 'Incident'}_${slug(incident?.EmployeeName)}`;
    pendingPrintRef.current = true;
    setPrintOptOpen(false);
  };
  const printAfterModalClosed = () => {
    if (!pendingPrintRef.current) return;   // a plain cancel must not print
    pendingPrintRef.current = false;
    window.print();
  };
  // Expiry dates stay editable once the record is complete, without reopening
  // it: the Edit Manager or the assigned reviewer, never the employee.
  const canEditExpiry  = !isNew && canEdit && !isSelfEmployee && isFinalStatus(incident?.Status);

  const saveExpiry = async (key, d) => {
    if (!d) return;
    try {
      await api.put(`/api/compliance/${id}`, { [key]: d.format('YYYY-MM-DD') });
      message.success('Expiry date updated.');
      const res = await api.get(`/api/compliance/${id}`);
      setIncident(res.data);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update the expiry date.');
    }
  };
  const isDateLocked   = checkLocked(lockConfig, isWriteup ? 'writeup' : 'compliance', incident?.DateOfIncident);

  const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
  const userOptions = (() => {
    const base = allUsers
      .filter((u) => u.IsActive && (vtIds.length === 0 || vtIds.includes(u.TeamID)))
      .map((u) => ({ label: u.TeamName ? `${u.DisplayName || u.Email} (${u.TeamName})` : (u.DisplayName || u.Email), value: u.ID }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // The record's employee when not on a Visible Team -- someone who "also
    // works for" one (Admin > Users) -- or inactive.
    if (!isNew && incident?.EmployeeUserID && !base.some((u) => u.value === incident.EmployeeUserID)) {
      const name = incident.EmployeeName || 'Unknown';
      base.unshift({ value: incident.EmployeeUserID,
        label: incident.EmployeeIsActive ? name : `${name} (inactive)` });
    }
    return base;
  })();

  const assignedToOptions = (() => {
    // assignUsers already filtered by CanEditIncidents >= 2 via API param
    const base = assignUsers
      .map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (incident?.AssignedToUserID && !base.some((u) => u.value === incident.AssignedToUserID)) {
      base.unshift({ value: incident.AssignedToUserID, label: incident.AssignedToName || 'Unknown' });
    }
    return base;
  })();

  const issueOptions = allIssues.map((iss) => ({ label: iss.Name, value: iss.ID }));

  // Read-only view of the record: shown on screen once the fields are locked,
  // and printed in place of the edit form for a draft or in-progress record.
  const renderReadOnly = (forPrint) => (
    <Descriptions column={1} bordered size="small" labelStyle={{ width: 230, fontWeight: 500, whiteSpace: 'nowrap' }}>
      <Descriptions.Item label="Created Date">{formatTS(incident.CreatedAt) || '—'}</Descriptions.Item>
      <Descriptions.Item label="Employee">
        {(currentUser?.CanViewAgentSummary ?? 0) >= 1
          ? <Link to={`/agents?user_id=${incident.EmployeeUserID}`}>{incident.EmployeeName}</Link>
          : <span>{incident.EmployeeName}</span>
        }
      </Descriptions.Item>
      <Descriptions.Item label="Team">{incident.TeamName || '—'}</Descriptions.Item>
      <Descriptions.Item label="Assigned To">{incident.AssignedToName || '—'}</Descriptions.Item>
      <Descriptions.Item label="Date of Incident">{incident.DateOfIncident || '—'}</Descriptions.Item>
      <Descriptions.Item label="Compliance Issue Types">
        <Space wrap size={4}>
          {(incident.Issues || []).length
            ? incident.Issues.map((iss) => <Tag key={iss.IssueID}>{iss.Name}</Tag>)
            : '—'}
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label="Warning Number">{incident.WarningNumber || '—'}</Descriptions.Item>
      {incident.WarningNumber && (
        <Descriptions.Item label="Warning Expires On">
          {(canEditExpiry && !forPrint)
            ? <DatePicker format="YYYY-MM-DD" allowClear={false}
                value={incident.WarningExpiresOn ? dayjs(incident.WarningExpiresOn) : null}
                onChange={(d) => saveExpiry('warning_expires_on', d)} />
            : (incident.WarningExpiresOn || '—')}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="QA Related">{incident.IsQARelated ? 'Yes' : 'No'}</Descriptions.Item>
      {!!incident.IsQARelated && (
        <Descriptions.Item label="QA Related Expires On">
          {(canEditExpiry && !forPrint)
            ? <DatePicker format="YYYY-MM-DD" allowClear={false}
                value={incident.QARelatedExpiresOn ? dayjs(incident.QARelatedExpiresOn) : null}
                onChange={(d) => saveExpiry('qa_related_expires_on', d)} />
            : (incident.QARelatedExpiresOn || '—')}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Description of Incident">
        <span style={{ whiteSpace: 'pre-wrap' }}>{incident.Description || '—'}</span>
      </Descriptions.Item>
      <Descriptions.Item label={actionPlanLabel}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{incident.AgreedActionPlan || '—'}</span>
      </Descriptions.Item>
      <Descriptions.Item label="Status">
        <Tag color={STATUS_COLOR[incident.Status]}>{incident.Status?.replace('_', ' ')}</Tag>
      </Descriptions.Item>
      {/* Only meaningful once the record is complete: that is when the
          deadline is stamped and when there is anything to acknowledge.
          Shown after acknowledgement too, since what the deadline was is
          part of the record. */}
      {isFinalStatus(incident.Status) && (
        <Descriptions.Item label="Acknowledge Deadline">
          {(() => {
            if (!incident.AcknowledgeDueDate) return <Typography.Text type="secondary">—</Typography.Text>;
            if (incident.AcknowledgedAt) return incident.AcknowledgeDueDate;
            const over = dayjs().isAfter(dayjs(incident.AcknowledgeDueDate), 'day');
            return (
              <span style={{ color: over ? '#ff4d4f' : '#fa8c16', fontWeight: over ? 600 : undefined }}>
                {incident.AcknowledgeDueDate}{over ? ' (overdue)' : ''}
              </span>
            );
          })()}
        </Descriptions.Item>
      )}
    </Descriptions>
  );

  return (
    <>
      <style>{`
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          .ant-layout { min-height: 0 !important; background: white !important; }
          .ant-layout-header { display: none !important; }
          .ant-layout-content { padding: 0 !important; margin: 0 !important; background: white !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          /* The record is one tall card: keeping a card whole pushed it to
             page 2 and left page 1 blank. Let cards split; keep each field,
             row and comment together instead. */
          .ant-card { break-inside: auto !important; page-break-inside: auto !important; }
          .ant-form-item, .ant-descriptions-row, .ant-list-item { break-inside: avoid; page-break-inside: avoid; }
          .ant-descriptions-bordered .ant-descriptions-view table {
            table-layout: auto !important;
            width: 100% !important;
          }
          .ant-descriptions-bordered .ant-descriptions-item-label,
          .ant-descriptions-bordered .ant-descriptions-item-content {
            white-space: normal !important;
            word-break: break-word !important;
            min-width: 60px !important;
            padding: 4px 8px !important;
          }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Print-only header */}
      {canPrint && (
        <div className="print-only" style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #000' }}>
          <div><strong>{title} #{id} &mdash; Status: {incident.Status?.replace('_', ' ')}</strong></div>
          <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>Printed: {dayjs().format('YYYY-MM-DD HH:mm')}</div>
        </div>
      )}

      <Space className="no-print" style={{ marginBottom: 12 }}>
        {canPrint && (
          <Button icon={<PrinterOutlined />} size="small" onClick={() => setPrintOptOpen(true)}>Print</Button>
        )}
        <Typography.Title level={4} style={{ margin: 0 }}>
          {isNew ? `New ${title}` : `${title} #${id}`}
        </Typography.Title>
        {!isNew && incident && (
          <Tag color={STATUS_COLOR[incident.Status]}>{incident.Status?.replace('_', ' ')}</Tag>
        )}
      </Space>

      {/* Sticky bar — Acknowledge + Set to Canceled */}
      {!isNew && isFinalStatus(incident?.Status) && (isManager || isSelfEmployee || isAssignedTo) && (
        <div className="no-print" style={{
          position: 'sticky', top: 0, zIndex: 20,
          background: 'var(--ant-color-bg-layout)',
          borderBottom: '1px solid var(--ant-color-border)',
          padding: '8px 0', marginBottom: 16,
          display: 'flex', gap: 8,
        }}>
          {!isAcknowledged && (isSelfEmployee || isAssignedTo || hasEditPerm) && (
            <Button type="primary" loading={acknowledging} onClick={handleAcknowledge}>
              Acknowledge
            </Button>
          )}
          {(isManager || (isAssignedTo && hasEditPerm)) && !isAcknowledged && !isDateLocked && incident?.Status === 'complete' && (
            <Popconfirm
              title="Reset this record to Draft?"
              description="The status will be set back to Draft for corrections."
              okText="Yes"
              cancelText="No"
              onConfirm={handleResetToDraft}
            >
              <Button loading={resetting}>Reset to Draft</Button>
            </Popconfirm>
          )}
          {(isManager || isAssignedTo) && !isDateLocked && (
            <Popconfirm
              title="Set this record to Canceled?"
              description="Canceling cannot be undone."
              okText="Yes"
              cancelText="No"
              onConfirm={handleSetCanceled}
            >
              <Button danger loading={canceling}>Set to Canceled</Button>
            </Popconfirm>
          )}
        </div>
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        {isSelfEmployee && !isAcknowledged && (
          <Alert
            type="warning"
            showIcon
            message="You are the employee on this record and cannot edit it."
            style={{ marginBottom: 16 }}
          />
        )}
        {isAcknowledged && (
          <Alert
            type="success"
            showIcon
            message={`Acknowledged on ${formatTS(incident.AcknowledgedAt)}.`}
            style={{ marginBottom: 16 }}
          />
        )}
        {isLocked && !isSelfEmployee && !isAcknowledged && (
          <Alert
            type="info"
            showIcon
            message="This record is locked because its status is complete, inactive or canceled."
            style={{ marginBottom: 16 }}
          />
        )}
        {!canEditFields && !isNew && incident ? (
          renderReadOnly(false)
        ) : (
          <>
          {!isNew && incident && <div className="print-only">{renderReadOnly(true)}</div>}
          <Form form={form} className="no-print" layout="vertical" initialValues={{ status: 'draft' }} onValuesChange={defaultExpiries}>
            {!isNew && incident && (
              <Form.Item label="Created Date">
                <span>{formatTS(incident.CreatedAt) || '—'}</span>
              </Form.Item>
            )}
            {!isNew && incident ? (
              <Form.Item label="Employee">
                {!isAgent
                  ? <Link to={`/agents?user_id=${incident.EmployeeUserID}`}>{incident.EmployeeName}</Link>
                  : <span>{incident.EmployeeName}</span>
                }
              </Form.Item>
            ) : (
              <Form.Item
                label="Employee" name="employee_user_id"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  showSearch placeholder="Select employee…"
                  options={userOptions}
                  filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                  onChange={(userId) => {
                    const user = allUsers.find((u) => u.ID === userId);
                    form.setFieldValue('team_id', user?.TeamID || undefined);
                    setEmployeeHasTeam(!!user?.TeamID);
                    setFormTeamId(user?.TeamID || null);
                  }}
                />
              </Form.Item>
            )}

            <Form.Item label="Team" name="team_id" rules={[{ required: true, message: 'Required' }]}>
              <Select
                showSearch placeholder="Select team…"
                options={[
                  ...allTeams.map((t) => ({ label: t.Name, value: t.ID })),
                  // The record's team when not one of mine (an "also works for" employee).
                  ...(incident?.TeamID && !allTeams.some((t) => t.ID === incident.TeamID)
                    ? [{ label: incident.TeamName || `Team ${incident.TeamID}`, value: incident.TeamID }] : []),
                ]}
                filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                disabled={!canEditFields || employeeHasTeam}
                onChange={(val) => setFormTeamId(val || null)}
              />
            </Form.Item>

            <Form.Item label="Assigned To" name="assigned_to_user_id" rules={[{ required: true, message: 'Required' }]}>
              <Select
                showSearch allowClear placeholder="Select supervisor…"
                options={assignedToOptions}
                filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                disabled={!canEditFields}
              />
            </Form.Item>

            <Form.Item label="Date of Incident" name="date_of_incident" rules={[{ required: true, message: 'Required' }]}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled={!canEditFields || isDateLocked} />
            </Form.Item>

            <Form.Item label="Compliance Issue Types" name="issue_ids" rules={[{ required: true, message: 'Required' }]}>
              <Select mode="multiple" allowClear placeholder="Select issues…" options={issueOptions} />
            </Form.Item>

            <Form.Item label="Warning Number" name="warning_number">
              <Select allowClear options={WARNING_OPTIONS} disabled={!canEditFields} />
            </Form.Item>

            <Form.Item noStyle dependencies={['warning_number']}>
              {({ getFieldValue }) => getFieldValue('warning_number') ? (
                <Form.Item label="Warning Expires On" name="warning_expires_on"
                  rules={[{ required: true, message: 'Required when a warning is selected' }]}>
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled={!canEditFields} />
                </Form.Item>
              ) : null}
            </Form.Item>

            <Form.Item label="QA Related" name="is_qa_related" valuePropName="checked" initialValue={false}>
              <Switch disabled={!canEditFields} />
            </Form.Item>

            <Form.Item noStyle dependencies={['is_qa_related']}>
              {({ getFieldValue }) => getFieldValue('is_qa_related') ? (
                <Form.Item label="QA Related Expires On" name="qa_related_expires_on"
                  rules={[{ required: true, message: 'Required when QA Related is on' }]}>
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled={!canEditFields} />
                </Form.Item>
              ) : null}
            </Form.Item>

            <Form.Item label="Description of Incident" name="description">
              <Input.TextArea
                rows={5} maxLength={4000} showCount
                placeholder="Describe the incident…"
                disabled={!canEditFields}
              />
            </Form.Item>

            <Form.Item label={actionPlanLabel} name="agreed_action_plan">
              <Input.TextArea
                rows={5} maxLength={4000} showCount
                disabled={!canEditFields}
              />
            </Form.Item>

            <Form.Item label="Status" name="status" rules={[{ required: true }]}>
              <Select options={EDIT_STATUS_OPTIONS} disabled={!canEditFields} onChange={handleStatusChange} />
            </Form.Item>

            {canEditFields && (
              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                  {isNew ? 'Create' : 'Save Changes'}
                </Button>
              </Form.Item>
            )}
          </Form>
          </>
        )}
      </Card>

      {/* ── Source links ──────────────────────────────────────────────────── */}
      {!isNew && incident?.Sources?.length > 0 && (
        <Card size="small" title={<Space><LinkOutlined /><span>Linked Sources</span></Space>}
          style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {incident.Sources.map((src, i) => {
              const label = SOURCE_LABEL[src.SourceType] ?? src.SourceType;
              const path  = SOURCE_PATH[src.SourceType];
              return (
                <div key={i}>
                  <Tag>{label}</Tag>
                  {path
                    ? <Link to={`${path}/${src.SourceID}`}>#{src.SourceID}</Link>
                    : <span>#{src.SourceID}</span>
                  }
                </div>
              );
            })}
          </Space>
        </Card>
      )}

      {/* ── Comments ──────────────────────────────────────────────────────── */}
      {!isNew && (
        <Card size="small" title="Comments" className={printComments ? '' : 'no-print'} style={{ marginBottom: 16 }}>
          {incident?.Comments?.length === 0 && (
            <Typography.Text type="secondary">No comments yet.</Typography.Text>
          )}
          <List
            dataSource={incident?.Comments || []}
            renderItem={(c) => (
              <List.Item style={{ alignItems: 'flex-start' }}>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} size="small" />}
                  title={<span style={{ fontSize: 12, fontWeight: 600 }}>{c.UserName} <Typography.Text type="secondary" style={{ fontWeight: 400 }}>{formatTS(c.CreatedAt)}</Typography.Text></span>}
                  description={
                    <>
                      <Typography.Text style={{ whiteSpace: 'pre-wrap' }}><LinkifiedText text={c.Comment} /></Typography.Text>
                      <CommentAttachments attachments={c.Attachments} />
                    </>
                  }
                />
              </List.Item>
            )}
          />
          {!(isAgent || isSelfEmployee) && !((isWriteup ? currentUser?.CanEditFormalWriteups : currentUser?.CanEditIncidents) === 1) && (
            <div ref={commentRef} className="no-print" style={{ marginTop: 8 }}>
              <CommentBox onSubmit={handleComment} loading={commenting} value={newComment} onChange={setNewComment} />
            </div>
          )}
        </Card>
      )}

      <Modal maskClosable={false}
        title="Print Options"
        open={printOptOpen}
        onCancel={() => setPrintOptOpen(false)}
        onOk={doPrint}
        okText="Print"
        okButtonProps={{ icon: <PrinterOutlined /> }}
        width={340}
        destroyOnClose
        afterClose={printAfterModalClosed}
      >
        <Space direction="vertical" style={{ padding: '8px 0' }} size={12}>
          <Checkbox checked={printComments} onChange={(e) => setPrintComments(e.target.checked)}>
            Include comments
          </Checkbox>
        </Space>
      </Modal>
    </>
  );
}
